import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ProjectFolderPanel from "@/components/ProjectFolderPanel";
import { Archive as ArchiveIcon, MagnifyingGlass, ArrowCounterClockwise, FolderOpen, User, CaretDown, CaretRight, DownloadSimple } from "@phosphor-icons/react";

export default function Archive() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [seller, setSeller] = useState("__all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = () => api.get("/projects/archived").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const unarchive = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Dezarhivezi proiectul „${p.code} — ${p.name}"? Revine în lista de proiecte active.`)) return;
    try {
      await api.post(`/projects/${p.id}/unarchive`);
      toast.success("Proiect dezarhivat");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sellers = useMemo(() => {
    const s = new Set((rows || []).map((p) => p.sales_owner_name).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const nlow = q.trim().toLowerCase();
  const filtered = (rows || []).filter((p) => {
    if (nlow && ![p.code, p.name, p.client, p.beneficiary].some((x) => (x || "").toLowerCase().includes(nlow))) return false;
    if (seller !== "__all" && (p.sales_owner_name || "") !== seller) return false;
    const d = (p.archived_at || "").slice(0, 10);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    return true;
  });

  const clearFilters = () => { setQ(""); setSeller("__all"); setDateFrom(""); setDateTo(""); };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="archive-page">
      <div className="mb-6 flex items-center gap-2">
        <ArchiveIcon size={24} weight="bold" className="text-primary" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Proiecte finalizate</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Arhivă proiecte</h1>
          <p className="text-sm text-muted-foreground">Proiectele finalizate și dosarele lor digitale. Nu apar în listele active.</p>
        </div>
      </div>

      {/* Legendă structură dosar */}
      <div className="mb-5 rounded-xl border border-border bg-white/[0.02] p-4" data-testid="archive-folder-legend">
        <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          <FolderOpen size={15} className="text-blue-400" /> Regula de organizare a dosarului digital
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-md bg-blue-500/15 px-2 py-1 font-medium text-blue-300">📁 Rezervor (poziție)</span>
          <CaretRight size={14} className="text-muted-foreground" />
          <span className="rounded-md bg-primary/15 px-2 py-1 font-medium text-primary">👤 Utilizator (cine a încărcat)</span>
          <CaretRight size={14} className="text-muted-foreground" />
          <span className="rounded-md bg-white/5 px-2 py-1 text-muted-foreground">📄 Documente</span>
        </div>
      </div>

      {/* Filtre */}
      <div className="mb-5 grid grid-cols-1 gap-3 rounded-xl border border-border bg-card p-4 md:grid-cols-4" data-testid="archive-filters">
        <div className="grid gap-1.5">
          <Label className="text-xs">Cod proiect / client</Label>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ex: HGS 1904" className="pl-9" data-testid="archive-search" />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Vânzător (a inițiat)</Label>
          <Select value={seller} onValueChange={setSeller}>
            <SelectTrigger data-testid="archive-seller"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Toți vânzătorii</SelectItem>
              {sellers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Finalizat de la</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="archive-date-from" />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">până la</Label>
          <div className="flex gap-2">
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="archive-date-to" />
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="archive-clear">Reset</Button>
          </div>
        </div>
      </div>

      {rows === null ? (
        <p className="text-sm text-muted-foreground">Se încarcă…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="archive-empty">
          Niciun proiect arhivat pentru filtrele alese.
        </div>
      ) : (
        <div className="space-y-4" data-testid="archive-list">
          {filtered.map((p) => {
            const isOpen = !!expanded[p.id];
            return (
              <div key={p.id} className="rounded-xl border border-border bg-card p-5" data-testid={`archive-card-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
                    <h3 className="font-head text-base font-bold leading-tight">{p.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{p.client || "—"}</span>
                      <span className="flex items-center gap-1"><User size={12} /> Vânzător: <span className="font-medium text-foreground">{p.sales_owner_name || "—"}</span></span>
                      <span>Finalizat: {fmtDateTime(p.archived_at) || "—"}</span>
                      <span className="font-medium text-foreground">{fmtMoney(p.budget)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))} data-testid={`archive-toggle-folder-${p.id}`}>
                      {isOpen ? <CaretDown size={14} className="mr-1" /> : <CaretRight size={14} className="mr-1" />} Dosar digital
                    </Button>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}/folder/export.zip?auth=${localStorage.getItem("hgs_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline" title="Descarcă dosarul ca ZIP (Rezervor/Utilizator)" data-testid={`archive-zip-${p.id}`}>
                      <DownloadSimple size={14} /> ZIP
                    </a>
                    <button onClick={() => navigate(`/projects/${p.id}`)} className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid={`archive-open-${p.id}`}>Deschide fișa</button>
                    {hasRole("admin") && (
                      <button onClick={(e) => unarchive(e, p)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`archive-unarchive-${p.id}`}>
                        <ArrowCounterClockwise size={14} /> Dezarhivează
                      </button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-4" data-testid={`archive-folder-${p.id}`}>
                    <ProjectFolderPanel projectId={p.id} positions={p.positions || []} readOnly />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
